import { Check, SquareArrowOutUpRight, MoreHorizontal } from "lucide-react";
import Link from "next/link";
import { Fragment } from "react";

import { CardActions } from "@/src/components/cards/card-actions";
import { ComparisonField } from "@/src/components/cards/comparison-field";
import { CopyableContent } from "@/src/components/copyable-content";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { Button } from "@/src/components/shadcn/ui/button";
import { DropdownMenu, DropdownMenuTrigger } from "@/src/components/shadcn/ui/dropdown-menu";
import {
  ValerieCardColSpan,
  ValerieCardField,
  ValerieCardSection,
  ValerieCardSections,
} from "@/src/components/valerie-card";
import { getNestedValue } from "@/src/lib/utils/object-utils";
import { compareStrings, getDisplayValue, normalizeForComparison, FieldType } from "@/src/lib/utils/string-utils";
import { PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES } from "@/src/models/person";

export interface Field {
  key: string;
  label: string;
  type?: FieldType;
  comparable?: boolean;
  required?: boolean;
  colSpan?: ValerieCardColSpan;
  startsRow?: boolean;
  section?: string;
  hideIfEmpty?: boolean;
  // Cards supply callbacks for different model types through this shared field descriptor.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue?: (obj: any) => string | null | undefined;
}

function resolveFieldValue(field: Field, obj: unknown): string | null | undefined {
  if (field.getValue) {
    return field.getValue(obj);
  }

  return getNestedValue(obj, field.key);
}

function isEmptyDisplayValue(displayValue: string) {
  return !displayValue.trim() || displayValue === "N/A" || PERSON_DEMOGRAPHIC_PLACEHOLDER_VALUES.has(displayValue);
}

function hasFieldValue(field: Field, obj: unknown) {
  const value = resolveFieldValue(field, obj);

  return !!value && !isEmptyDisplayValue(getDisplayValue(value, field.type ?? "text"));
}

export interface FieldDisplayProps {
  field: Field;
  primaryValue: string | null;
  secondaryValue?: string | null;
  isCompareMode: boolean;
  compareAgainstTitle?: string;
}

export function FieldDisplay({
  field,
  primaryValue,
  secondaryValue,
  isCompareMode,
  compareAgainstTitle = "EMR",
}: FieldDisplayProps) {
  if (isCompareMode && secondaryValue !== null && field.comparable) {
    return (
      <ComparisonField
        label={field.label}
        colSpan={field.colSpan}
        startsRow={field.startsRow}
        value={primaryValue || "N/A"}
        secondaryValue={secondaryValue || "N/A"}
        type={field.type ?? "text"}
        compareAgainstTitle={compareAgainstTitle}
      />
    );
  }

  const displayValue = getDisplayValue(primaryValue, field.type ?? "text");

  return (
    <ValerieCardField label={field.label} colSpan={field.colSpan} startsRow={field.startsRow}>
      {isEmptyDisplayValue(displayValue) ? (
        <span className="text-sm text-muted-foreground/60">—</span>
      ) : (
        <CopyableContent text={displayValue} />
      )}
    </ValerieCardField>
  );
}

function groupFieldsBySection(fields: Field[]) {
  const groups: { section?: string; fields: Field[] }[] = [];

  for (const field of fields) {
    const current = groups.at(-1);

    if (current && current.section === field.section) {
      current.fields.push(field);
    } else {
      groups.push({ section: field.section, fields: [field] });
    }
  }

  return groups;
}

export function FieldLayout({
  fields,
  patient,
  secondaryObject,
  compareMode = false,
  compareAgainstTitle = "EMR",
  emptyMessage = "No details available",
  hideEmptyFields = true,
  renderField,
}: {
  fields: Field[];
  patient: unknown;
  secondaryObject?: unknown;
  compareMode?: boolean;
  compareAgainstTitle?: string;
  emptyMessage?: string | null;
  hideEmptyFields?: boolean;
  renderField?: (field: Field) => React.ReactNode | undefined;
}) {
  const comparing = compareMode && !!secondaryObject;
  const visibleFields = fields.filter(
    (field) =>
      (!hideEmptyFields && !field.hideIfEmpty) ||
      field.required ||
      hasFieldValue(field, patient) ||
      (comparing && field.comparable && hasFieldValue(field, secondaryObject))
  );

  if (visibleFields.length === 0) {
    return emptyMessage ? <p className="text-sm text-muted-foreground">{emptyMessage}</p> : null;
  }

  return (
    <ValerieCardSections>
      {groupFieldsBySection(visibleFields).map((group) => (
        <ValerieCardSection key={`${group.section ?? ""}:${group.fields[0].key}`} title={group.section}>
          {group.fields.map((field) => (
            <Fragment key={field.key}>
              {renderField?.(field) ?? (
                <FieldDisplay
                  field={field}
                  primaryValue={resolveFieldValue(field, patient) || "N/A"}
                  secondaryValue={secondaryObject ? resolveFieldValue(field, secondaryObject) || "N/A" : null}
                  isCompareMode={compareMode}
                  compareAgainstTitle={compareAgainstTitle}
                />
              )}
            </Fragment>
          ))}
        </ValerieCardSection>
      ))}
    </ValerieCardSections>
  );
}

export function hasConflicts(fields: Field[], obj: unknown, secondaryObj: unknown) {
  const comparableFields = fields.filter((field) => field.comparable);

  return comparableFields.some((field) => {
    const fieldType = field.type ?? "text";
    const primaryValue = normalizeForComparison(getNestedValue(obj, field.key) || "", fieldType);
    const secondaryValue = normalizeForComparison(getNestedValue(secondaryObj, field.key) || "", fieldType);

    return !compareStrings(primaryValue, secondaryValue, fieldType);
  });
}

export function renderBadge(compareMode: boolean, secondaryObject: unknown, hasAnyConflicts: boolean) {
  if (!compareMode) {
    return null;
  }

  if (!secondaryObject) {
    return <MissingDataBadge />;
  }

  if (hasAnyConflicts) {
    return <ConflictsBadge />;
  }

  return <SyncedBadge />;
}

export function SyncedBadge() {
  return (
    <Badge variant="outline" className="text-success border-success/30 bg-success/10">
      <Check className="h-3 w-3 mr-1" />
      Synced
    </Badge>
  );
}

export function ConflictsBadge() {
  return (
    <Badge variant="outline" className="text-warn border-warn/30 bg-warn/10">
      Conflicts
    </Badge>
  );
}

export function EhrDataBadge() {
  return (
    <Badge variant="outline" className="bg-ehr/10 text-ehr">
      EHR Data
    </Badge>
  );
}

export function MissingDataBadge() {
  return (
    <Badge variant="outline" className="text-destructive border-destructive/30 bg-destructive/10">
      Missing EHR Data
    </Badge>
  );
}

export function LinkToEntity({ href }: { href: string }) {
  return (
    <Link className="text-muted-foreground hover:text-foreground" href={href} target="_blank" rel="noopener noreferrer">
      <SquareArrowOutUpRight className="size-4" />
    </Link>
  );
}

export function EntityDropdownMenu({
  cardActionsProps,
  customProps = [],
}: {
  cardActionsProps: {
    id: string;
    label: string;
    href?: string;
    customProps?: Array<{ label: string; onClick: () => void }>;
  };
  customProps?: Array<{ label: string; onClick: () => void }>;
}) {
  const mergedCustomProps = [...(cardActionsProps.customProps ?? []), ...customProps];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex h-7 w-7 p-0 text-muted-foreground hover:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
        >
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <CardActions {...cardActionsProps} isContextMenu={false} customProps={mergedCustomProps} />
    </DropdownMenu>
  );
}
